import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonTable from '../../../common/table/ComonTable';
import PageHeadingSection from '../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../services/apiService';
import Breadcrumbs from '../../../common/BreadCrum/BreadCrum';
import storageService from '../../../../utils/storageService';

const PollList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filters
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const fetchPolls = async () => {
        setLoading(true);
        try {
            const employeeCode = storageService.getItem("employeeCode");
            if (!employeeCode) {
                toast.error("User not found in session");
                setLoading(false);
                return;
            }

            const userRes = await apiServices({
                method: "POST",
                url: `/ResidentialUser/GetResidentialUserById`,
                data: { userId: employeeCode }
            });

            if (userRes.success || userRes.isSuccess || userRes.IsSuccess || userRes) {
                const apiData = userRes.data || userRes;
                const user = (apiData?.data && apiData.data[0]) || (Array.isArray(apiData) ? apiData[0] : null);
                
                if (user?.siteId || user?.SiteId) {
                    const siteId = user.siteId || user.SiteId;
                    
                    const pollRes = await apiServices({
                        method: "GET",
                        url: `/ResidentialPollMaster/GetAllResidentialPollsBySiteId/${siteId}`
                    });
                    
                    let pData = pollRes.data || pollRes;
                    if (pData?.data) pData = pData.data;
                    
                    if (Array.isArray(pData)) {
                        setPolls(pData.reverse());
                        setPageInfo(prev => ({
                            ...prev,
                            totalRecords: pData.length,
                            totalPages: Math.ceil(pData.length / prev.pageSize)
                        }));
                    } else {
                        setPolls([]);
                    }
                } else {
                    toast.error("Site ID not found in user details");
                }
            }
        } catch (error) {
            console.error("Error fetching polls:", error);
            toast.error("Failed to fetch polls.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPolls();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const handleDelete = async (row) => {
        if (!window.confirm("Are you sure you want to delete this poll?")) return;
        try {
            const pollId = row.PollId;
            const res = await apiServices({
                url: `/ResidentialPollMaster/deleteResidentialPoll/${pollId}`,
                method: 'DELETE'
            });
            if (res.success || res.isSuccess || res.IsSuccess || res) {
                toast.success("Poll deleted successfully");
                fetchPolls();
            } else {
                toast.error("Failed to delete poll");
            }
        } catch (error) {
            console.error("Error deleting poll:", error);
            toast.error("An error occurred while deleting");
        }
    };

    const handleEdit = (row) => {
        const id = row.PollId;
        navigate(`/zentres/Poll-Management/poll-form/${id}`, { state: { id, item: row } });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const tableHeaders = [
        { label: "No", key: "no", align: "left" },
        { label: "Title", key: "title", align: "left" },
        { label: "Start Date", key: "startDate", align: "left" },
        { label: "End Date", key: "endDate", align: "left" },
        { label: "Total Votes", key: "totalVotes", align: "center" },
        { label: "Status", key: "status", align: "center" },
        { label: "Actions", key: "actions", align: "center" }
    ];

    const renderCell = (key, row, index) => {
        switch (key) {
            case "no":
                return <span className="text-[13px] font-medium text-[#64748B]">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "title":
                return <span className="text-[13px] font-bold text-gray-800 tracking-tight">{row.Title || "—"}</span>;
            case "startDate":
                return <span className="text-[13px] font-medium text-[#64748B]">{formatDate(row.StartDate)}</span>;
            case "endDate":
                return <span className="text-[13px] font-medium text-[#64748B]">{formatDate(row.EndDate)}</span>;
            case "totalVotes":
                return <span className="text-[13px] font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-md">{row.TotalVotes || 0}</span>;
            case "status":
                const status = row.Status || 'Inactive';
                let statusColor = "bg-gray-100 text-gray-600";
                if (status === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (status === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold shadow-sm ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null; // Handled by CommonTable
            default:
                return <span className="text-[13px] font-medium text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const filteredData = useMemo(() => {
        let data = polls;
        if (searchText) {
            data = data.filter(item => (item.Title || "").toLowerCase().includes(searchText.toLowerCase()));
        }
        if (statusFilter) {
            data = data.filter(item => item.Status === statusFilter);
        }
        return data;
    }, [polls, searchText, statusFilter]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredData.slice(start, start + pageInfo.pageSize);
    }, [filteredData, currentPage, pageInfo.pageSize]);

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                    type="text"
                    placeholder="Search Polls..."
                    value={searchText}
                    onChange={(e) => {
                        setSearchText(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="w-full sm:w-60 pl-9 pr-4 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
                />
            </div>

            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-40 px-3 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Poll Management"
                subHeading="Manage community polls and view responses"
                primaryBtnText="Create Poll"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate('/zentres/Poll-Management/poll-form/add')}
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    tableHeading="Polls List"
                    headerAction={headerAction}
                    headers={tableHeaders}
                    data={paginatedData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row) => renderCell(key, row, row.originalIndex)}
                    pagination={{
                        ...pageInfo,
                        pageNumber: currentPage,
                        totalRecords: filteredData.length,
                        totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                    }}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            </div>
        </div>
    );
};

export default PollList;
